import { PhoneInput } from '@globalmed/atlas-ui';
import { CSSProperties, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useListStore } from '@/stores/list';
import { DEFAULT_COUNTRY } from '@/utils/constants';

type TPhoneField = {
  country: string | undefined;
  number: string | undefined;
};

type TFormValues = Record<string, TPhoneField>;

type TProps = {
  name?: string;
  required?: boolean;
  sx?: CSSProperties;
  disabled?: boolean;
  isError?: boolean;
  message?: string;
  label?: string;
};

/**
 * Renders a phone input field with an optional country dropdown, integrated with React Hook Form.
 * @param props - Component props.
 * @param props.name - The field name in the form (default: "phone").
 * @param props.required - Marks the field as required (default: false).
 * @param props.sx - Custom CSS properties to apply to the input.
 * @param props.disabled - Disables the input if true (default: false).
 * @param props.isError - Displays error state if true (default: false).
 * @param props.message - Error or helper message to display below the input.
 * @param props.label - Label for the input field.
 * @returns PhoneField component.
 */
export const PhoneField = ({
  name = 'phone',
  required = false,
  sx,
  disabled = false,
  isError = false,
  message,
  label,
}: TProps) => {
  const { t } = useTranslation();
  const { control, setValue, getValues, trigger } =
    useFormContext<TFormValues>();
  const countries = useListStore((state) => state.countries);

  const handleCountryChange = useCallback(
    (country: string) => {
      setValue(`${name}.country`, country, {
        shouldDirty: true,
      });
      void trigger(`${name}.country`);
    },
    [setValue, trigger, name],
  );

  const phoneNumber = getValues(`${name}.number`) ?? '';
  const countryId = getValues(`${name}.country`) || DEFAULT_COUNTRY;

  return (
    <Controller
      key={`${name}-${countryId}`}
      name={`${name}.number`}
      control={control}
      render={({ field, fieldState }) => (
        <PhoneInput
          country={countryId}
          countries={countries.map(({ phonecode, ...other }) => ({
            ...other,
            phonecode: phonecode ?? '',
          }))}
          value={phoneNumber ?? ''}
          placeholder={'(555) 000-0000'}
          label={label ?? t('common.phone-number')}
          isError={isError || !!fieldState.error}
          message={
            message ??
            (fieldState.error?.message ? t(fieldState.error.message) : '')
          }
          sx={{ width: '100%', ...sx }}
          onChange={(value) => {
            const finalValue = value === '' ? undefined : value;
            field.onChange(finalValue);

            if (!getValues(`${name}.country`)) {
              setValue(`${name}.country`, DEFAULT_COUNTRY, {
                shouldDirty: true,
              });
            }
          }}
          onCountryChange={handleCountryChange}
          required={required}
          disabled={disabled}
        />
      )}
    />
  );
};
